import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchMyProfile, identityKeys } from '@/lib/queries/identity';
import { SettingsErrorShell } from '@/components/settings/SettingsErrorShell';
import { AccountForm, type AccountFormReferrer } from './-components/AccountForm';

type AccountSearch = { from?: AccountFormReferrer };

export const Route = createFileRoute('/_authed/settings/account/')({
  // `?from=profile` is set when a member lands here via "Edit profile"
  // on their own profile page. The back link on this screen swaps to
  // point at /profile in that case (default: /settings).
  validateSearch: (raw: Record<string, unknown>): AccountSearch => {
    const f = raw.from;
    return f === 'profile' ? { from: 'profile' } : {};
  },
  component: SettingsAccountPage,
});

function SettingsAccountPage() {
  const { from } = Route.useSearch();
  const query = useQuery({
    queryKey: identityKeys.me(),
    queryFn: fetchMyProfile,
    retry: false,
  });

  if (query.isLoading) return null;
  if (query.isError || !query.data) {
    return <SettingsErrorShell title="Account" message="Could not load account." />;
  }
  const data = query.data;
  return (
    <AccountForm
      {...(from ? { from } : {})}
      userId={data.id}
      firstName={data.firstName}
      lastName={data.lastName ?? ''}
      email={data.email ?? ''}
      jobTitle={data.jobTitle ?? ''}
      company={data.company ?? ''}
      industry={data.industry ?? ''}
      bio={data.bio ?? ''}
      careerStage={data.careerStage ?? null}
      openTo={data.openTo ?? []}
      photoUrl={data.photoUrl}
      {...(data.coverPhotoUrl !== undefined ? { coverPhotoUrl: data.coverPhotoUrl } : {})}
      gender={data.gender ?? null}
      dateOfBirth={data.dateOfBirth ?? null}
      phoneNumber={data.phoneNumber ?? null}
      educationDegree={data.educationDegree ?? null}
      educationField={data.educationField ?? null}
      educationSchool={data.educationSchool ?? null}
      educationGradYear={data.educationGradYear ?? null}
      socialLinks={data.socialLinks ?? null}
      place={data.place ?? null}
      askMeAbout={data.askMeAbout ?? null}
      lookingToLearn={data.lookingToLearn ?? null}
      surpriseFact={data.surpriseFact ?? null}
    />
  );
}
